import { Box } from 'reflexbox';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import * as graphql from 'generated/graphql';

import { LockIcon } from '@treams/treams-icons';
import { Paragraph, StatusBar, Title, Subheading, Label } from '@treams/treams-ui';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import { EngagementReply } from './EngagementReply';
import { EngagementReplyInput } from './EngagementReplyInput';

const Container = styled(Box)`
	display: flex;
	flex-direction: column;
	align-items: center;
`;

const AnswerLabel = styled(Subheading)`
	color: ${(props) => props.theme.colors.antracite};
`;

const AnswerContent = styled(Paragraph)`
	padding: 16px;
	border-radius: 5px;
	color: ${(props) => props.theme.colors.antracite};
	background: ${({ theme }) => theme.colors.lightBlueLight};
	overflow-wrap: break-word;
	hyphens: auto;
`;

export default function EngagementReplies(props: { answerManagerID: string }) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const [answerID, managerLabel] = props.answerManagerID.split(':');
	const managerID = managerLabel === 'super' || !managerLabel ? null : managerLabel;

	const { data, refetch } = graphql.useEngagementAnswerWithRepliesQuery({
		variables: {
			id: answerID,
			managerID,
		},
	});
	const [createReply] = graphql.useCreateEngagementReplyMutation();

	const answer = data?.openQuestionAnswerWithReplies;

	// Only initiating employee can see the owner, hence its replies are anonymous.
	const isAnonymous = answer ? answer?.owner !== null : false;

	const [firstName, ...lastNames] = t('engagement.replies.anonymousEmployeeName').split(' ');
	const anonymousUser = answer?.owner ?? {
		firstName,
		lastName: lastNames.join(' '),
	};

	return (
		<Container>
			{isAnonymous && (
				<StatusBar
					icon={<LockIcon />}
					text={
						<>
							{t('engagement.respond.anonymous.title.0')}{' '}
							<strong>{t('engagement.respond.anonymous.title.1')}</strong>.
						</>
					}
				/>
			)}
			{answer && (
				<Box flexGrow={1} maxWidth={670} my={5}>
					<Title my={4}>{localize(answer.question.title).text}</Title>
					<AnswerLabel mb={2}>
						{answer.owner
							? t('engagement.replies.answer.yours')
							: t('engagement.replies.answer.theirs')}
					</AnswerLabel>
					<AnswerContent>{answer.text}</AnswerContent>
					<Box pl={3} mt={4}>
						<Label mb={3}>
							{answer.owner ? t('engagement.replies.label') : t('engagement.replies.label')}
						</Label>
						{answer.replies.length === 0 && (
							<Paragraph textAlign="center">{t('engagement.replies.noReplies')}</Paragraph>
						)}
						{answer.replies.map((reply: graphql.EngagementReply) => (
							<EngagementReply
								key={reply.id}
								reply={reply}
								anonymousUser={anonymousUser as graphql.User}
							/>
						))}
						<Box mt={4}>
							<EngagementReplyInput
								onSubmit={async (text) => {
									await createReply({
										variables: {
											input: {
												answerID: answer.id,
												managerID,
												text,
											},
										},
									});

									await refetch();
								}}
								anonymous={isAnonymous}
							/>
						</Box>
					</Box>
				</Box>
			)}
		</Container>
	);
}
